/**
 * Mapping Prestation (Equipement, Service, Confort, Activite, ActiviteSportive, ActiviteCulturelle)
 * → icône par enumKey (prioritaire) ou id (fallback). Par défaut : bold/essentional_ui/check_circle.
 */

export type PrestationType =
  | "equipement"
  | "service"
  | "confort"
  | "activite"
  | "activiteSportive"
  | "activiteCulturelle";

export type PrestationRef = { id: number; enumKey?: string };

const DEFAULT_ICON = "bold/essentional_ui/check_circle";

/** Par type : enumKey → icône */
const ENUM_KEY_ICONS: Record<PrestationType, Record<string, string>> = {
  equipement: {
    PRESTATION_EQUIPEMENT_PISCINE: "bold/sports/swimming",
    PRESTATION_EQUIPEMENT_TERRAIN_CLOS_COMMUN: "bold/home_furniture/barrier",
    PRESTATION_EQUIPEMENT_PARKING_PRIVE: "bold/home_furniture/parking",
    PRESTATION_EQUIPEMENT_JEUX_ENFANTS: "bold/home_furniture/playground",
    PRESTATION_EQUIPEMENT_PLAGE_EQUIPEE_PRIVEE: "bold/home_furniture/deck",
    PRESTATION_EQUIPEMENT_TERRASSE: "bold/home_furniture/deck",
    PRESTATION_EQUIPEMENT_CAMPING_CAR_EMPLACEMENT:
      "bold/essentional_ui/check_circle",
    PRESTATION_EQUIPEMENT_TERRAIN_CLOS: "bold/home_furniture/barrier",
    PRESTATION_EQUIPEMENT_DEVELOPPEMENT_DURABLE: "bold/astronomy/earth",
    PRESTATION_EQUIPEMENT_AIRE_JEUX: "bold/home_furniture/playground",
    PRESTATION_EQUIPEMENT_ASCENCEUR: "bold/building_infrastructure/elevator",
    PRESTATION_EQUIPEMENT_CARAVANEIGE: "bold/essentional_ui/check_circle",
    PRESTATION_EQUIPEMENT_CAMPING_CAR_AIRE_ACCUEIL:
      "bold/essentional_ui/check_circle",
    PRESTATION_EQUIPEMENT_CAMPING_CAR_AIRE_SERVICE:
      "bold/essentional_ui/check_circle",
    PRESTATION_EQUIPEMENT_PARKING: "bold/home_furniture/parking",
    PRESTATION_EQUIPEMENT_GARAGE: "bold/building_infrastructure/garage",
  },
  service: {
    PRESTATION_SERVICE_TABLE_HOTE: "bold/food_kitchen/restauration",
    PRESTATION_SERVICE_DEMI_PENSION: "bold/food_kitchen/restauration",
    PRESTATION_SERVICE_GESTION_LIBRE: "bold/essentional_ui/check_circle",
    PRESTATION_SERVICE_PETIT_DEJEUNER_CHAMBRE: "bold/food_kitchen/tea_cup",
    PRESTATION_SERVICE_PETIT_DEJEUNER: "bold/food_kitchen/tea_cup",
    PRESTATION_SERVICE_ANIMAUX_ACCEPTES: "bold/essentional_ui/paw",
    PRESTATION_SERVICE_ANIMAUX_ACCEPTES_SUPPLEMENT: "bold/money/euro",
    PRESTATION_SERVICE_PENSION_COMPLETE: "bold/food_kitchen/restauration",
    PRESTATION_SERVICE_WIFI:
      "bold/network_it_programming/wi-fi_router_minimalistic",
    PRESTATION_SERVICE_LOCATION_LINGE: "bold/home_furniture/draps",
    PRESTATION_SERVICE_RESTAURATION: "bold/food_kitchen/restauration",
    PRESTATION_SERVICE_BUNGATOILE: "bold/essentional_ui/check_circle",
    PRESTATION_SERVICE_CARAVANES: "bold/essentional_ui/check_circle",
    PRESTATION_SERVICE_MOBILHOME: "bold/essentional_ui/check_circle",
    PRESTATION_SERVICE_HLL: "bold/essentional_ui/check_circle",
    PRESTATION_SERVICE_LOCATION_DRAPS: "bold/home_furniture/draps",
  },
  confort: {
    PRESTATION_CONFORT_TELEVISION: "bold/electronic_devices/monitor",
    PRESTATION_CONFORT_WIFI:
      "bold/network_it_programming/wi-fi_router_minimalistic",
  },
  activite: {},
  activiteSportive: {
    ACTIVITE_SPORTIVE_PRESTATION_SKI: "bold/sports/downhill_skiing",
  },
  activiteCulturelle: {},
};

/** Par type : id → icône (fallback quand enumKey absent ou non mappé) */
const ID_ICONS: Record<PrestationType, Record<number, string>> = {
  equipement: {
    972: "bold/home_furniture/parking",
    1093: "bold/sports/swimming",
    723: "bold/home_furniture/deck",
    1321: "bold/home_furniture/parking",
    1323: "bold/home_furniture/parking",
    1322: "bold/home_furniture/parking",
    672: "bold/home_furniture/deck",
    838: "bold/home_furniture/changing_station",
    1320: "bold/home_furniture/parking",
    943: "bold/home_furniture/fridge",
    1060: "bold/home_furniture/playground",
    741: "bold/home_furniture/wc",
    1106: "bold/food_kitchen/restauration",
    1004: "bold/home_furniture/dine_in",
    5592: "bold/home_furniture/parking",
    1300: "bold/home_furniture/meeting_room",
    1045: "bold/essentional_ui/check_circle",
    1042: "bold/nature_travel/leaf",
    1053: "bold/essentional_ui/check_circle",
    1103: "bold/essentional_ui/check_circle",
    803: "bold/food_kitchen/outdoor_grill",
    774: "bold/home_furniture/vacuum",
    799: "bold/medicine/no_smoke",
    1014: "bold/essentional_ui/family",
    1012: "bold/home_furniture/iron",
    876: "bold/food_kitchen/restauration",
    981: "bold/home_furniture/sofa",
    4054: "bold/food_kitchen/restauration",
    971: "bold/home_furniture/balcony",
    900: "bold/essentional_ui/check_circle",
    893: "bold/home_furniture/condicioner_2",
    902: "bold/home_furniture/shower",
    907: "bold/food_kitchen/hoven",
    895: "bold/home_furniture/fridge",
    946: "bold/weather/wind",
    922: "bold/home_furniture/dishwasher",
    974: "bold/home_furniture/bath_private",
    904: "bold/home_furniture/bed",
    1011: "bold/home_furniture/single_bed",
    1015: "bold/home_furniture/high_chair",
    997: "bold/food_kitchen/range_hood",
    973: "bold/home_furniture/wc",
    983: "bold/home_furniture/sofa_3",
    842: "bold/network_it_programming/wi-fi_router_minimalistic",
    1013: "bold/home_furniture/bed",
    4063: "bold/home_furniture/board_game",
    824: "bold/home_furniture/heat",
    796: "bold/home_furniture/bath",
    1028: "bold/home_furniture/draps",
    926: "bold/food_kitchen/tea_cup",
    710: "bold/home_furniture/barrier",
    715: "bold/home_furniture/bar",
    951: "bold/home_furniture/heat",
    877: "bold/home_furniture/chimney",
    612: "bold/home_furniture/playground",
    624: "bold/building_infrastructure/elevator",
    1030: "bold/astronomy/earth",
    5061: "bold/home_furniture/bed",
    1138: "bold/notes/info",
    1166: "bold/home_furniture/dry",
    661: "bold/home_furniture/sofa_3",
    1010: "bold/food_kitchen/tea_cup",
    1044: "bold/home_furniture/playground",
    1050: "bold/building_infrastructure/garage",
    1127: "bold/food_kitchen/restauration",
    1508: "bold/sports/swimming",
    998: "bold/video_audio_sound/clapperboard_open_play",
    1479: "bold/nature_travel/leaf",
    1085: "bold/essentional_ui/check_circle",
    1083: "bold/essentional_ui/check_circle",
    1378: "bold/essentional_ui/trash_bin_trash",
    917: "bold/home_furniture/washing_machine_minimalistic",
    949: "bold/home_furniture/washing_machine",
    1477: "bold/nature_travel/leaf",
    3694: "bold/home_furniture/bed",
    5691: "bold/food_kitchen/tea_cup",
    5915: "bold/food_kitchen/kettle",
    993: "bold/money/ticket",
    613: "bold/nature_travel/leaf",
    860: "bold/essentional_ui/filter",
    5048: "bold/transport_parts_service/electric_refueling",
    1504: "bold/sports/swimming",
    1505: "bold/sports/swimming",
    698: "bold/home_furniture/sauna",
    957: "bold/home_furniture/bath",
    985: "bold/home_furniture/speaker",
    960: "bold/call/phone",
    1172: "bold/money/ticket",
    1212: "bold/food_kitchen/fastfood",
    1216: "bold/food_kitchen/restauration",
    1217: "bold/food_kitchen/restauration",
    1016: "bold/home_furniture/changing_station",
    984: "bold/medicine/heart_pulse",
    1421: "bold/building_infrastructure/garage",
    977: "bold/home_furniture/bath",
    1318: "bold/building_infrastructure/garage",
    6597: "bold/home_furniture/bath",
    827: "bold/home_furniture/barrier",
    5068: "bold/home_furniture/deck",
    5069: "bold/nature_travel/leaf",
    6801: "bold/food_kitchen/tea_cup",
    4055: "bold/food_kitchen/restauration",
    1130: "bold/school/document",
    1190: "bold/essentional_ui/flag_2",
    767: "bold/shopping_ecommerce/shop",
    6877: "bold/home_furniture/stroller",
    916: "bold/home_furniture/meeting_room",
    781: "bold/transport_parts_service/bus",
    939: "bold/electronic_devices/tv",
    4554: "bold/sports/tennis",
    1064: "bold/security/lock",
    996: "bold/video_audio_sound/volume_cross",
    785: "bold/essentional_ui/check_circle",
    1025: "bold/money/euro",
    761: "bold/home_furniture/wc",
    721: "bold/nature_travel/leaf",
    604: "bold/essentional_ui/check_circle",
    805: "bold/essentional_ui/check_circle",
    1089: "bold/nature_travel/leaf",
    6487: "bold/home_furniture/shower",
    850: "bold/home_furniture/wc",
    6732: "bold/network_it_programming/wi-fi_router_minimalistic",
    941: "bold/home_furniture/bath",
    756: "bold/network_it_programming/wi-fi_router_minimalistic",
    811: "bold/sports/downhill_skiing",
    648: "bold/essentional_ui/check_circle",
    673: "bold/electronic_devices/tv",
    732: "bold/home_furniture/balcony",
    736: "bold/essentional_ui/check_circle",
    739: "bold/home_furniture/wc",
    762: "bold/nature_travel/leaf",
    706: "bold/essentional_ui/check_circle",
    759: "bold/essentional_ui/check_circle",
    760: "bold/essentional_ui/check_circle",
    828: "bold/essentional_ui/check_circle",
    832: "bold/essentional_ui/check_circle",
    835: "bold/essentional_ui/check_circle",
    836: "bold/essentional_ui/check_circle",
    869: "bold/essentional_ui/check_circle",
    870: "bold/essentional_ui/check_circle",
  },
  service: {
    687: "bold/essentional_ui/paw",
    1183: "bold/food_kitchen/restauration",
    1127: "bold/food_kitchen/restauration",
    837: "bold/essentional_ui/check_circle",
    923: "bold/food_kitchen/tea_cup",
    926: "bold/food_kitchen/tea_cup",
    1025: "bold/money/euro",
    1130: "bold/school/document",
    847: "bold/home_furniture/bed",
    1029: "bold/home_furniture/dry",
    1138: "bold/notes/info",
    1166: "bold/home_furniture/dry",
    661: "bold/home_furniture/sofa_3",
    1010: "bold/food_kitchen/tea_cup",
    1044: "bold/home_furniture/playground",
    1050: "bold/building_infrastructure/garage",
    880: "bold/home_furniture/bed",
    6801: "bold/food_kitchen/tea_cup",
    988: "bold/food_kitchen/chef_hat",
    4060: "bold/home_furniture/bath",
    986: "bold/home_furniture/heat",
    797: "bold/network_it_programming/station",
    1089: "bold/nature_travel/leaf",
    939: "bold/electronic_devices/tv",
    993: "bold/money/ticket",
    5068: "bold/home_furniture/deck",
    1083: "bold/essentional_ui/check_circle",
    1026: "bold/shopping_ecommerce/shop",
    6877: "bold/home_furniture/stroller",
    916: "bold/home_furniture/meeting_room",
    781: "bold/transport_parts_service/bus",
    767: "bold/shopping_ecommerce/shop",
    1212: "bold/food_kitchen/fastfood",
    1172: "bold/money/ticket",
    1216: "bold/food_kitchen/restauration",
    1217: "bold/food_kitchen/restauration",
    1016: "bold/home_furniture/changing_station",
    785: "bold/essentional_ui/check_circle",
    1115: "bold/network_it_programming/wi-fi_router_minimalistic",
    849: "bold/essentional_ui/check_circle",
    930: "bold/essentional_ui/check_circle",
    1028: "bold/home_furniture/draps",
    1145: "bold/home_furniture/draps",
    4770: "bold/shopping_ecommerce/cart_large_2",
  },
  confort: {
    963: "bold/electronic_devices/monitor",
    999: "bold/food_kitchen/microwave",
    842: "bold/network_it_programming/wi-fi_router_minimalistic",
    922: "bold/home_furniture/dishwasher",
    919: "bold/home_furniture/washing_machine_minimalistic",
    943: "bold/home_furniture/fridge",
    672: "bold/home_furniture/deck",
    838: "bold/home_furniture/changing_station",
    1015: "bold/home_furniture/high_chair",
    997: "bold/food_kitchen/range_hood",
    876: "bold/food_kitchen/restauration",
    882: "bold/network_it_programming/wi-fi_router_minimalistic",
    1013: "bold/home_furniture/bed",
    1028: "bold/home_furniture/draps",
    904: "bold/home_furniture/bed",
    1011: "bold/home_furniture/single_bed",
    5915: "bold/food_kitchen/kettle",
    606: "bold/sports/bicycling_round",
    1029: "bold/home_furniture/dry",
    951: "bold/home_furniture/heat",
    877: "bold/home_furniture/chimney",
    981: "bold/home_furniture/sofa",
    4054: "bold/food_kitchen/restauration",
    893: "bold/home_furniture/condicioner_2",
    987: "bold/home_furniture/heat",
    796: "bold/home_furniture/bath",
    1145: "bold/home_furniture/draps",
    824: "bold/home_furniture/heat",
    971: "bold/home_furniture/balcony",
    6804: "bold/home_furniture/draps",
    985: "bold/home_furniture/speaker",
    960: "bold/call/phone",
    973: "bold/home_furniture/wc",
    1010: "bold/food_kitchen/tea_cup",
    900: "bold/essentional_ui/check_circle",
    898: "bold/food_kitchen/restauration",
    741: "bold/home_furniture/wc",
    983: "bold/home_furniture/sofa_3",
    3694: "bold/home_furniture/bed",
    5691: "bold/food_kitchen/tea_cup",
    5940: "bold/food_kitchen/tea_cup",
    986: "bold/home_furniture/heat",
    902: "bold/home_furniture/shower",
    974: "bold/home_furniture/bath_private",
    977: "bold/home_furniture/bath",
    949: "bold/home_furniture/washing_machine",
    917: "bold/home_furniture/washing_machine_minimalistic",
    950: "bold/home_furniture/washing_machine",
  },
  activite: {},
  activiteSportive: {},
  activiteCulturelle: {},
};

/**
 * Retourne l'icône pour une prestation : enumKey en priorité, puis id en fallback.
 */
export function getPrestationIconName(
  type: PrestationType,
  ref: PrestationRef | undefined,
): string {
  if (!ref) return DEFAULT_ICON;
  const enumMap = ENUM_KEY_ICONS[type];
  const idMap = ID_ICONS[type];
  const enumKey = ref.enumKey?.trim() ?? "";
  if (enumKey && enumMap[enumKey]) return enumMap[enumKey];
  const id = typeof ref.id === "number" ? ref.id : undefined;
  if (id !== undefined && idMap[id] !== undefined) return idMap[id];
  return DEFAULT_ICON;
}
